import {
  ProColumns,
  ProForm,
  ProFormDatePicker,
  ProFormDigit,
  type ProFormInstance,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
} from '@ant-design/pro-components';
import React, { useState } from 'react';
import { IconModal } from '../IconModal';
import { IconSelect } from '../IconSelect';
import type { FormScene, MyProColumn } from '../MyProTable';

export function TableFormItem<T>(
  formRef: React.RefObject<ProFormInstance> | undefined,
  columns: MyProColumn<T>[],
  scene: FormScene,
  record?: Partial<T>,
) {
  // 处理每一个item项
  function renderFormItemByColumn<T>(
    col: MyProColumn<T>,
    scene: FormScene,
    record?: Partial<T>,
  ) {
    const name = col.dataIndex as string;
    const label = col.title as any;

    const fieldProps =
      typeof col.fieldProps === 'function'
        ? col.fieldProps({}, { type: scene } as any)
        : col.fieldProps;

    // 你可按 valueType 扩展更多类型
    switch (col.valueType) {
      case 'select':
        return (
          <>
            <ProFormSelect
              key={name}
              name={name}
              label={label}
              valueEnum={col.valueEnum as any}
              request={col.request as any}
              allowClear={name === 'status' ? false : true}
              // fieldProps={fieldProps}
              fieldProps={{
                onChange: (value, option: any) => {
                  if (name === 'roleId') {
                    // 同步写入另一个字段
                    formRef?.current?.setFieldsValue({
                      roleCode: option?.code,
                    });
                  }
                },
              }}
            />

            {/* 多绑定一个字段 */}
            {name === 'roleId' && <ProFormText name="roleCode" hidden />}
          </>
        );
      case 'switch':
        return (
          <ProFormSwitch
            key={name}
            name={name}
            label={label}
            fieldProps={fieldProps}
          />
        );
      case 'digit':
        return (
          <ProFormDigit
            key={name}
            name={name}
            label={label}
            fieldProps={fieldProps}
          />
        );
      case 'date':
      case 'dateTime':
        return (
          <ProFormDatePicker
            key={name}
            name={name}
            label={label}
            fieldProps={fieldProps}
          />
        );
      // case 'icon':
      //   return (
      //     <ProForm.Item
      //       key={name}
      //       name={name}
      //       label={label}
      //       rules={col.formRules}
      //     >
      //       <IconSelect allowClear placeholder="请选择图标" />
      //     </ProForm.Item>
      //   );
      case 'icon':
        return (
          <ProForm.Item
            key={name}
            name={name}
            label={label}
            rules={col.formRules}
          >
            <IconModal />
          </ProForm.Item>
        );
      default:
        return (
          <ProFormText
            key={name}
            name={name}
            label={label}
            fieldProps={fieldProps}
          />
        );
    }
  }

  return columns
    .filter((c) => c.dataIndex) // 必须要有字段名
    .filter((c) => {
      if (scene === 'edit' && c.hideInEdit) return false;
      if (scene === 'create' && c.hideInCreate) return false;
      return true;
    })
    .map((c) => {
      const name = c.dataIndex as string;

      const disabled =
        scene === 'edit'
          ? !!c.editDisabled
          : scene === 'create'
            ? !!c.createDisabled
            : false;

      const formRules =
        typeof c.formRules === 'function'
          ? c.formRules(scene, record)
          : c.formRules;

      const formItemProps =
        typeof c.formItemProps === 'function'
          ? c.formItemProps(scene, record)
          : c.formItemProps;

      // 把禁用注入进去（不破坏原 fieldProps）
      const mergedCol: MyProColumn<T> = {
        ...c,
        fieldProps: {
          ...(typeof c.fieldProps === 'object' ? c.fieldProps : {}),
          disabled,
        },
      };

      const node = renderFormItemByColumn(mergedCol, scene, record);

      // 给 node 补 rules / formItemProps（不同组件 prop 名一致，ProForm* 都支持 rules）
      return React.cloneElement(node as any, {
        rules: formRules,
        ...formItemProps,
        key: name,
      });
    });
}
